import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { withTypes, FormProps } from "react-final-form";

import Alert from "@mui/material/Alert";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import {
  Grid,
  Button,
  DialogContent,
  DialogActions,
  TextField,
  Theme,
} from "@mui/material";

import Field from "components/Field";
import {
  ProductVersion,
  ISaveProductVersion,
} from "services/KnowfullyAdminClient";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const { Form } = withTypes<ISaveProductVersion>();

type ProductVersionFormProps = {
  productVersion?: ProductVersion;
  submitLabel: string;
  onSubmit: (values: ISaveProductVersion) => void;
  onCancel?: () => void;
};

const ProductVersionForm = ({
  productVersion,
  submitLabel,
  onCancel,
  onSubmit,
}: ProductVersionFormProps & FormProps<ISaveProductVersion>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const init = useMemo(() => productVersion ?? {}, [productVersion]);

  return useMemo(
    () => (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "nameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="nameText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="value" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField variant="standard"
                        fullWidth
                        label={t({ id: "valueLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        data-cy="valueText"
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button disabled={submitting} onClick={onCancel} data-cy="cancelButton">
                {t({ id: "cancelButton" })}
              </Button>

              <Button type="submit" color="primary" disabled={submitting} data-cy="submitButton">
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    ),
    [styles.feedback, init, onCancel, onSubmit, submitLabel, t]
  );
};

export default ProductVersionForm;
